<template>
    <form-wrapper class="intake-providers" @submit="handleFormSubmission(data)">
        <p v-if="!data.providers.length" class="intake-providers__empty">
            {{ $t('forms.intake.treating_providers.empty') }}
        </p>

        <div v-for="(provider, index) in data.providers" :key="`provider-${index}`" class="intake-providers__card">
            <div class="intake-providers__card-header">
                <h4 class="intake-providers__card-title">
                    {{ $t('forms.intake.treating_providers.provider_label', { number: index + 1 }) }}
                </h4>
                <common-button
                    :label="$t('common.microcopy.remove')"
                    type="secondary"
                    size="small"
                    :isSubmittable="false"
                    @click="removeProvider(index)"
                ></common-button>
            </div>

            <form-row>
                <form-cell>
                    <text-field
                        :label="$t('forms.intake.treating_providers.fields.provider_name.label')"
                        :placeholder="$t('forms.intake.treating_providers.fields.provider_name.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        :valuePath="`providers.${index}.providerName`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.intake.treating_providers.fields.specialty.label')"
                        :placeholder="$t('forms.intake.treating_providers.fields.specialty.placeholder')"
                        :options="specialtyOptions"
                        :formData="data"
                        :valuePath="`providers.${index}.specialty`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treating_providers.fields.practice_facility.label')"
                        :placeholder="$t('forms.intake.treating_providers.fields.practice_facility.placeholder')"
                        :formData="data"
                        :valuePath="`providers.${index}.practiceFacility`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treating_providers.fields.first_seen.label')"
                        type="date"
                        :formData="data"
                        :valuePath="`providers.${index}.firstSeen`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treating_providers.fields.last_seen.label')"
                        type="date"
                        :isDisabled="Boolean(data.providers[index].stillTreating)"
                        :formData="data"
                        :valuePath="`providers.${index}.lastSeen`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <checkbox-field
                        :formData="data"
                        :valuePath="`providers.${index}.stillTreating`"
                        @change="onStillTreatingChange(index, $event)"
                    >
                        <template #label>
                            <span>{{ $t('forms.intake.treating_providers.fields.still_treating.label') }}</span>
                        </template>
                    </checkbox-field>
                </form-cell>
            </form-row>

            <div class="intake-providers__regions">
                <label class="form-field__label">
                    {{ $t('forms.intake.treating_providers.fields.body_regions.label') }}
                </label>
                <div class="intake-providers__regions-grid">
                    <checkbox-input
                        v-for="option in bodyRegionOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="isBodyRegionChecked(index, option.value)"
                        @change="toggleBodyRegion(index, option.value)"
                    ></checkbox-input>
                </div>
            </div>

            <form-row>
                <form-cell>
                    <checkbox-field
                        :formData="data"
                        :valuePath="`providers.${index}.locateRecordsAuthorized`"
                        @change="change($event)"
                    >
                        <template #label>
                            <span>{{ $t('forms.intake.treating_providers.fields.locate_records.label') }}</span>
                        </template>
                    </checkbox-field>
                </form-cell>
            </form-row>
        </div>

        <button class="intake-providers__add" type="button" @click="addProvider">
            + {{ $t('forms.intake.treating_providers.actions.add_provider') }}
        </button>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'

    const BODY_REGIONS = [
        'head',
        'neck',
        'upper_back',
        'mid_back',
        'low_back',
        'right_shoulder',
        'left_shoulder',
        'right_arm',
        'left_arm',
        'right_elbow',
        'left_elbow',
        'right_wrist_hand',
        'left_wrist_hand',
        'chest',
        'abdomen',
        'right_hip',
        'left_hip',
        'right_thigh',
        'left_thigh',
        'right_knee',
        'left_knee',
        'right_leg',
        'left_leg',
        'right_ankle_foot',
        'left_ankle_foot',
        'other',
    ]

    const SPECIALTIES = [
        'emergency_medicine',
        'primary_care',
        'orthopedics',
        'neurosurgery',
        'neurology',
        'pain_management',
        'physical_therapy',
        'chiropractic',
        'other',
    ]

    export default {
        mixins: [FormMixin],

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    providers: [],
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            specialtyOptions() {
                return SPECIALTIES.map((value) => ({
                    label: this.$t(`forms.intake.treating_providers.fields.specialty.options.${value}`),
                    value,
                }))
            },

            bodyRegionOptions() {
                return BODY_REGIONS.map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.body_region.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            createProvider() {
                return {
                    providerName: undefined,
                    specialty: undefined,
                    practiceFacility: undefined,
                    firstSeen: undefined,
                    lastSeen: undefined,
                    stillTreating: false,
                    bodyRegions: [],
                    locateRecordsAuthorized: false,
                }
            },

            addProvider() {
                this.change({
                    path: 'providers',
                    value: [...this.data.providers, this.createProvider()],
                })
            },

            removeProvider(index) {
                const next = [...this.data.providers]
                next.splice(index, 1)
                this.change({ path: 'providers', value: next })
            },

            isBodyRegionChecked(index, region) {
                const regions = this.data.providers[index]?.bodyRegions || []
                return regions.includes(region)
            },

            toggleBodyRegion(index, region) {
                const current = [...(this.data.providers[index]?.bodyRegions || [])]
                const nextRegions = current.includes(region) ? current.filter((item) => item !== region) : [...current, region]

                this.change({
                    path: `providers.${index}.bodyRegions`,
                    value: nextRegions,
                })
            },

            onStillTreatingChange(index, event) {
                this.change(event)
                if (event.value) {
                    this.change({
                        path: `providers.${index}.lastSeen`,
                        value: undefined,
                    })
                }
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .intake-providers__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-providers__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;

        @include breakpoint(tablet) {
            padding: rem(12px);
        }
    }

    .intake-providers__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-providers__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-providers__regions {
        margin: rem(20px) 0;
    }

    .intake-providers__regions-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: rem(10px) rem(12px);
        margin-top: rem(10px);

        @include breakpoint(tablet) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @include breakpoint(smartphone) {
            grid-template-columns: 1fr;
        }
    }

    .intake-providers__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }
</style>
